'use client';

import { c as _c } from "react/compiler-runtime";
import clsx from 'clsx';
import { FC, useEffect, useRef, useState } from 'react';
import { useGridInitialAnimation } from '@/components/CaseStudiesPage/GridCaseStudies/hooks/useGridInitialAnimation';
import { mapCaseToSlideItem } from '@/components/CaseStudiesPage/mapCaseToSlideItem';
import { WorksSectionDto } from '@/components/service-pages/WorksSection/types';
import WorkCard from '@/components/service-pages/WorksSection/WorkCard';
import AnimatedButton from '@/components/shared/AnimatedButton';
import TitleGraffiti from '@/components/shared/TitleGraffiti';
import { CardPreviewUsageProvider, useCardPreviewUsage } from '@/contexts/CardPreviewUsageContext';
import { useCustomCursor } from '@/hooks/useCustomCursor';
import { gsap, ScrollTrigger } from '@/lib/gsap';
import { CaseStudyDto } from '@/types/case-studies';
import css from './style.module.scss';
const INITIAL_CASES_COUNT = 5;
interface Props {
  data: WorksSectionDto;
  cases: CaseStudyDto[];
  isIframe?: boolean;
  htmlId?: string;
}
const WorksSectionContent: FC<Props> = t0 => {
  const $ = _c(28);
  const {
    data,
    cases,
    isIframe,
    htmlId
  } = t0;
  const {
    setCursorVariant,
    setIsCursorVisible
  } = useCustomCursor();
  const takeCardPreview = useCardPreviewUsage();
  const gridRef = useRef(null);
  const [showAll, setShowAll] = useState(false);
  let t1;
  if ($[0] !== takeCardPreview) {
    t1 = caseStudy => mapCaseToSlideItem(takeCardPreview(caseStudy, `works-section:${caseStudy.id}`));
    $[0] = takeCardPreview;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const previews = cases.flatMap(t1);
  const displayedCases = showAll ? previews : previews.slice(0, INITIAL_CASES_COUNT);
  const hasMore = previews.length > INITIAL_CASES_COUNT;
  useGridInitialAnimation({
    wrapperRef: gridRef,
    displayedItems: displayedCases,
    cssItemsSelector: `.${css.caseItem}`,
    disabled: isIframe
  });
  let t2;
  if ($[2] !== hasMore || $[3] !== isIframe || $[4] !== showAll) {
    t2 = [showAll, hasMore, isIframe];
    $[2] = hasMore;
    $[3] = isIframe;
    $[4] = showAll;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  useEffect(() => {
    if (!showAll || !hasMore || isIframe || !gridRef.current) {
      return;
    }
    const runAfterLayout = () => {
      ScrollTrigger.refresh();
      const items = gridRef.current?.querySelectorAll(`.${css.caseItem}`);
      if (!items?.length) {
        return;
      }
      const newItems = Array.from(items).slice(INITIAL_CASES_COUNT);
      if (newItems.length === 0) {
        return;
      }
      gsap.to(newItems, {
        y: 0,
        opacity: 1,
        duration: 1.5,
        ease: "power3.out",
        stagger: 0.15,
        overwrite: true
      });
    };
    const id = requestAnimationFrame(() => {
      requestAnimationFrame(runAfterLayout);
    });
    return () => cancelAnimationFrame(id);
  }, t2);
  if (!previews.length) {
    return null;
  }
  const t3 = css;
  let t4;
  if ($[6] !== setCursorVariant) {
    t4 = () => setCursorVariant("lottie-green-visit-case");
    $[6] = setCursorVariant;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  let t5;
  if ($[8] !== data.title) {
    t5 = data.title ? <TitleGraffiti className={css.titleWrapper} url="/assets/video/mp4/work-graffiti.mp4" title={data.title} /> : null;
    $[8] = data.title;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  const t6 = css;
  let t7;
  if ($[10] !== setIsCursorVisible || $[11] !== showAll) {
    t7 = (caseItem, index) => <WorkCard key={caseItem.id} caseItem={caseItem} setIsCustomCursorVisible={setIsCursorVisible} className={clsx(css.caseItem, showAll && index >= INITIAL_CASES_COUNT && css.caseItemReveal)} />;
    $[10] = setIsCursorVisible;
    $[11] = showAll;
    $[12] = t7;
  } else {
    t7 = $[12];
  }
  const t8 = displayedCases.map(t7);
  let t9;
  if ($[13] !== gridRef || $[14] !== t6.casesGrid || $[15] !== t8) {
    t9 = <ul ref={gridRef} className={t6.casesGrid}>{t8}</ul>;
    $[13] = gridRef;
    $[14] = t6.casesGrid;
    $[15] = t8;
    $[16] = t9;
  } else {
    t9 = $[16];
  }
  let t10;
  if ($[17] !== hasMore || $[18] !== setShowAll || $[19] !== showAll) {
    t10 = hasMore && !showAll ? <div className={css.showMoreWrapper}><AnimatedButton className={css.showMoreBtn} onClick={() => setShowAll(true)}>Show more</AnimatedButton></div> : null;
    $[17] = hasMore;
    $[18] = setShowAll;
    $[19] = showAll;
    $[20] = t10;
  } else {
    t10 = $[20];
  }
  let t11;
  if ($[21] !== htmlId || $[22] !== t10 || $[23] !== t3.wrapper || $[24] !== t4 || $[25] !== t5 || $[26] !== t9) {
    t11 = <section className={t3.wrapper} id={htmlId} onMouseEnter={t4}>{t5}{t9}{t10}</section>;
    $[21] = htmlId;
    $[22] = t10;
    $[23] = t3.wrapper;
    $[24] = t4;
    $[25] = t5;
    $[26] = t9;
    $[27] = t11;
  } else {
    t11 = $[27];
  }
  return t11;
};
const WorksSectionClient: FC<Props> = props => {
  const $ = _c(2);
  let t0;
  if ($[0] !== props) {
    t0 = <CardPreviewUsageProvider><WorksSectionContent {...props} /></CardPreviewUsageProvider>;
    $[0] = props;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  return t0;
};
export default WorksSectionClient;